import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import type { Theme } from '@mui/material/styles';
import {
  type Lang,
  type Locale,
  getLanguage,
  getTenantLanguages,
} from '@realadvisor/agency-utils';
import copy from 'clipboard-copy';
import {
  Check,
  Copy,
  Globe,
  Info,
  Link2,
  Mail,
  MessageCircle,
} from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { Review_Invitation_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import { onboardingTransactionFieldSx } from '@modules/_shared/components/create-transaction/onboardingFormStyles';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAgentReviewPageUrl } from '@modules/_shared/utils/agentUrls';
import {
  BRAND_GRADIENT,
  BRAND_GRADIENT_HOVER,
  OnboardingInputIcon,
  OnboardingSelectChevronIcon,
  onboardingDeckCompactSelectTextFieldSx,
  onboardingDeckOutlinedTextFieldSx,
} from '@modules/onboarding/OnboardingDeckShared';
import { ANALYTICS_EVENTS, useAnalytics } from '@shared/analytics';
import { useLocale } from '@shared/hooks/locale';
import { languageMenuOptions } from '@shared/LocaleContext';
import {
  type ReviewType,
  getDefaultReviewMessage,
} from '@shared/utils/review-messages';

import {
  GET_REVIEW_INVITATION_TEMPLATE,
  UPSERT_REVIEW_INVITATION_TEMPLATE,
} from '../visibilityReviewsQueries';

const deckFieldLabelSx = {
  display: 'block',
  mb: 0.75,
  fontSize: 13,
  fontWeight: 600,
  color: 'text.primary',
  letterSpacing: 0.1,
} as const;

const deckMessageFieldSx = {
  ...onboardingDeckOutlinedTextFieldSx,
  width: '100%',
  '& .MuiOutlinedInput-root': {
    alignItems: 'flex-start',
    py: 0,
  },
  '& textarea.MuiOutlinedInput-input': {
    py: '12px',
    px: '12px',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.45,
    boxSizing: 'border-box',
  },
} as const;

const GET_USER_SLUG = gql(/* GraphQL */ `
  query getUserSlug($id: uuid!) {
    users_by_pk(id: $id) {
      id
      agent_slug
      show_in_agency_pages
      first_name
      last_name
    }
  }
`);

const languageMap = Object.fromEntries(
  languageMenuOptions.map(option => [option.value, option.label]),
) as Record<Lang, string>;

export type ReviewInvitationContentProps = {
  initialAgentId?: string | null;
  allowCopyBeforePublish?: boolean;
  embedded?: boolean;
  tenantId?: string | null;
};

const draftKey = (type: ReviewType, language: Locale | null): string | null =>
  language == null ? null : `${type}|${language}`;

const withoutLink = (text: string, link: string | null): string =>
  link == null ? text : text.replace(` ${link}`, '').replace(link, '');

const withLink = (base: string, link: string | null): string =>
  link == null
    ? base
    : base === ''
    ? link
    : /https?:\/\//.test(base)
    ? base
    : `${base} ${link}`;

export const ReviewInvitationContent = ({
  initialAgentId,
  allowCopyBeforePublish = false,
  embedded = false,
  tenantId,
}: ReviewInvitationContentProps) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const { track } = useAnalytics();

  const effectiveTenantId = me?.tenant_id ?? tenantId ?? null;

  const availableLanguages = useMemo(
    () =>
      effectiveTenantId != null
        ? getTenantLanguages(effectiveTenantId) ?? []
        : [],
    [effectiveTenantId],
  );

  const selectedAgent = initialAgentId ?? me?.id ?? '';

  const { data: userSlugData, loading: userSlugLoading } = useQuery(
    GET_USER_SLUG,
    {
      variables: { id: selectedAgent },
      skip: selectedAgent === '',
    },
  );

  const [selectedLanguage, setSelectedLanguage] = useState<Locale | null>(
    availableLanguages.find(lang => lang === locale.toLowerCase()) ??
      availableLanguages[0] ??
      null,
  );
  const [selectedType, setSelectedType] = useState<ReviewType>('seller');

  const [message, setMessage] = useState<string>(() =>
    getDefaultReviewMessage('seller', getLanguage(selectedLanguage ?? 'en')),
  );
  const [savedTemplateMessage, setSavedTemplateMessage] = useState<
    string | null
  >(null);
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [templateSavedToast, setTemplateSavedToast] = useState(false);
  const [hasManuallyEdited, setHasManuallyEdited] = useState(false);
  const [saveError, setSaveError] = useState<ErrorLike | null>(null);
  const [unsavedDrafts, setUnsavedDrafts] = useState<Record<string, string>>(
    {},
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const linkCopyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const templateSavedTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const editedTuplesRef = useRef<Set<string>>(new Set());
  const prevSelectionRef = useRef<{
    type: ReviewType;
    language: Locale | null;
  }>({ type: selectedType, language: selectedLanguage });

  useEffect(
    () => () => {
      clearTimeout(timeoutRef.current ?? undefined);
      clearTimeout(linkCopyTimeoutRef.current ?? undefined);
      clearTimeout(templateSavedTimeoutRef.current ?? undefined);
    },
    [],
  );

  const { data: templateData, loading: templateLoading } = useQuery(
    GET_REVIEW_INVITATION_TEMPLATE,
    {
      variables: {
        brokerId: selectedAgent,
        type: selectedType as Review_Invitation_Type_Enum_Enum,
        language: selectedLanguage ?? '',
      },
      skip: selectedLanguage == null || selectedAgent === '' || me == null,
    },
  );

  const [upsertTemplate] = useMutation(UPSERT_REVIEW_INVITATION_TEMPLATE, {
    refetchQueries: [
      {
        query: GET_REVIEW_INVITATION_TEMPLATE,
        variables: {
          brokerId: selectedAgent,
          type: selectedType as Review_Invitation_Type_Enum_Enum,
          language: selectedLanguage ?? '',
        },
      },
    ],
    onError: setSaveError,
  });

  const languagesOptions = useMemo(
    () =>
      availableLanguages.map(lg => ({
        value: lg,
        label: languageMap[getLanguage(lg)] ?? lg,
      })),
    [availableLanguages],
  );

  const defaultMessage = useMemo(
    () =>
      getDefaultReviewMessage(
        selectedType,
        getLanguage(selectedLanguage ?? 'en'),
      ),
    [selectedType, selectedLanguage],
  );

  const reviewLink = useMemo(() => {
    const user = userSlugData?.users_by_pk;
    if (effectiveTenantId == null || selectedLanguage == null) {
      return null;
    }

    return getAgentReviewPageUrl(
      effectiveTenantId,
      selectedLanguage,
      user?.agent_slug,
      user?.id ?? (allowCopyBeforePublish ? selectedAgent : null),
    );
  }, [
    effectiveTenantId,
    userSlugData?.users_by_pk,
    allowCopyBeforePublish,
    selectedAgent,
    selectedLanguage,
  ]);

  const messageForTemplate = withoutLink(message, reviewLink);

  const hasUnsavedChanges =
    hasManuallyEdited &&
    messageForTemplate !== savedTemplateMessage &&
    messageForTemplate !== defaultMessage;

  const reviewLinkRef = useRef(reviewLink);
  useEffect(() => {
    const previousLink = reviewLinkRef.current;
    reviewLinkRef.current = reviewLink;

    if (reviewLink == null) {
      return;
    }
    setMessage(prev => withLink(withoutLink(prev, previousLink), reviewLink));
  }, [reviewLink]);

  useEffect(() => {
    if (templateLoading) {
      return;
    }

    const prev = prevSelectionRef.current;
    const tupleChanged =
      prev.type !== selectedType || prev.language !== selectedLanguage;

    let draftsForLookup = unsavedDrafts;
    if (tupleChanged && hasManuallyEdited) {
      const prevKey = draftKey(prev.type, prev.language);
      if (prevKey != null && messageForTemplate !== savedTemplateMessage) {
        draftsForLookup = { ...unsavedDrafts, [prevKey]: messageForTemplate };
        setUnsavedDrafts(draftsForLookup);
      }
    }
    prevSelectionRef.current = {
      type: selectedType,
      language: selectedLanguage,
    };

    const template = templateData?.review_invitation_templates?.[0];
    const templateMatchesSelection =
      template?.language === selectedLanguage &&
      template?.type === selectedType;
    const savedMessage = templateMatchesSelection ? template?.message : null;

    const newKey = draftKey(selectedType, selectedLanguage);
    const stashedDraft = newKey != null ? draftsForLookup[newKey] : undefined;
    const link = reviewLinkRef.current;

    if (stashedDraft != null) {
      setMessage(withLink(stashedDraft, link));
      setSavedTemplateMessage(savedMessage ?? null);
      setSaveForFuture(false);
      setHasManuallyEdited(true);
    } else if (savedMessage != null && savedMessage.trim() !== '') {
      setMessage(withLink(savedMessage, link));
      setSavedTemplateMessage(savedMessage);
      setSaveForFuture(false);
      setHasManuallyEdited(false);
    } else {
      setMessage(withLink(defaultMessage, link));
      setSavedTemplateMessage(null);
      setSaveForFuture(false);
      setHasManuallyEdited(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedType,
    selectedLanguage,
    templateData,
    templateLoading,
    defaultMessage,
  ]);

  const handleMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setHasManuallyEdited(true);

      if (embedded) {
        const tupleKey = draftKey(selectedType, selectedLanguage);
        if (tupleKey != null && !editedTuplesRef.current.has(tupleKey)) {
          editedTuplesRef.current.add(tupleKey);
          track(ANALYTICS_EVENTS.ONBOARDING_REVIEWS_TEMPLATE_EDITED, {
            type: selectedType,
            language: selectedLanguage,
          });
        }
      }

      setMessage(e.target.value);
    },
    [embedded, selectedLanguage, selectedType, track],
  );

  const handleSaveTemplate = useCallback(async () => {
    if (
      me == null ||
      selectedLanguage == null ||
      selectedAgent === '' ||
      messageForTemplate.trim() === ''
    ) {
      return;
    }

    const result = await upsertTemplate({
      variables: {
        brokerId: selectedAgent,
        type: selectedType as Review_Invitation_Type_Enum_Enum,
        language: selectedLanguage,
        message: messageForTemplate,
      },
    });

    if (result.data) {
      setSavedTemplateMessage(messageForTemplate);
      setSaveForFuture(false);
      setHasManuallyEdited(false);
      const tupleKey = draftKey(selectedType, selectedLanguage);
      if (tupleKey != null) {
        setUnsavedDrafts(prev => {
          if (!(tupleKey in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[tupleKey];
          return next;
        });
      }
      if (embedded) {
        track(ANALYTICS_EVENTS.ONBOARDING_REVIEWS_TEMPLATE_SAVED, {
          type: selectedType,
          language: selectedLanguage,
        });
        setTemplateSavedToast(true);
        if (templateSavedTimeoutRef.current) {
          clearTimeout(templateSavedTimeoutRef.current);
        }
        templateSavedTimeoutRef.current = setTimeout(
          () => setTemplateSavedToast(false),
          2400,
        );
      }
    }
  }, [
    embedded,
    me,
    messageForTemplate,
    selectedAgent,
    selectedLanguage,
    selectedType,
    track,
    upsertTemplate,
  ]);

  const handleCopyMessage = useCallback(async () => {
    try {
      await copy(message);
      setCopied(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);

      if (embedded) {
        track(ANALYTICS_EVENTS.ONBOARDING_REVIEWS_COPIED, {
          type: selectedType,
          language: selectedLanguage,
          mode: 'message',
        });
      }

      if (saveForFuture) {
        await handleSaveTemplate();
      }
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  }, [
    embedded,
    message,
    handleSaveTemplate,
    saveForFuture,
    selectedLanguage,
    selectedType,
    track,
  ]);

  const handleCopyLink = useCallback(async () => {
    if (reviewLink == null) {
      return;
    }
    try {
      await copy(reviewLink);
      setLinkCopied(true);
      if (linkCopyTimeoutRef.current) {
        clearTimeout(linkCopyTimeoutRef.current);
      }
      linkCopyTimeoutRef.current = setTimeout(() => setLinkCopied(false), 2000);
      if (embedded) {
        track(ANALYTICS_EVENTS.ONBOARDING_REVIEWS_COPIED, {
          type: selectedType,
          language: selectedLanguage,
          mode: 'link',
        });
      }
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  }, [embedded, reviewLink, selectedLanguage, selectedType, track]);

  const handleShareWhatsapp = useCallback(() => {
    if (reviewLink == null) {
      return;
    }
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    if (embedded) {
      track(ANALYTICS_EVENTS.ONBOARDING_REVIEWS_SHARED, {
        type: selectedType,
        language: selectedLanguage,
        channel: 'whatsapp',
      });
    }
  }, [embedded, message, reviewLink, selectedLanguage, selectedType, track]);

  const handleShareEmail = useCallback(() => {
    if (reviewLink == null) {
      return;
    }
    const subject = t("I'd love your review");
    const url = `mailto:?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(message)}`;
    window.location.href = url;
    if (embedded) {
      track(ANALYTICS_EVENTS.ONBOARDING_REVIEWS_SHARED, {
        type: selectedType,
        language: selectedLanguage,
        channel: 'email',
      });
    }
  }, [embedded, message, reviewLink, selectedLanguage, selectedType, t, track]);

  const handleEmbeddedTypeChange = useCallback(
    (next: ReviewType) => {
      if (next === selectedType) {
        return;
      }
      setSelectedType(next);
      track(ANALYTICS_EVENTS.ONBOARDING_REVIEWS_TYPE_CHANGED, { type: next });
    },
    [selectedType, track],
  );

  const handleEmbeddedLanguageChange = useCallback(
    (next: Locale) => {
      if (next === selectedLanguage) {
        return;
      }
      setSelectedLanguage(next);
      track(ANALYTICS_EVENTS.ONBOARDING_REVIEWS_LANGUAGE_CHANGED, {
        language: next,
      });
    },
    [selectedLanguage, track],
  );

  const handleTypeChange = useCallback(
    (_: React.SyntheticEvent, value: ReviewType) => setSelectedType(value),
    [],
  );

  const isProfilePublished =
    userSlugData?.users_by_pk?.show_in_agency_pages === true;

  const canCopy =
    reviewLink != null &&
    (allowCopyBeforePublish || (!userSlugLoading && isProfilePublished));

  const reviewTypes: ReviewType[] = ['seller', 'buyer'];

  if (embedded) {
    return (
      <>
        <Paper
          elevation={0}
          className="onboarding-review-invitation"
          sx={{
            borderRadius: '12px',
            border: 1,
            borderColor: 'grey.200',
            bgcolor: 'background.paper',
            overflow: 'hidden',
            ...onboardingTransactionFieldSx,
            '& .onboarding-review-message-field .MuiOutlinedInput-root': {
              py: 0,
            },
            '& .onboarding-review-message-field .MuiOutlinedInput-input': {
              py: '12px',
              px: '12px',
            },
          }}
        >
          <Stack
            spacing={2}
            sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1.5, md: 2 } }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'nowrap',
                width: '100%',
                minWidth: 0,
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ flexShrink: 0, minWidth: 0 }}
              >
                {reviewTypes.map(type => {
                  const isActive = selectedType === type;
                  return (
                    <Chip
                      key={type}
                      label={type === 'seller' ? t('Seller') : t('Buyer')}
                      clickable
                      onClick={() => handleEmbeddedTypeChange(type)}
                      sx={{
                        height: 32,
                        borderRadius: '999px',
                        fontWeight: isActive ? 700 : 600,
                        fontSize: 13,
                        border: '1.5px solid',
                        ...(isActive
                          ? {
                              bgcolor: '#203097',
                              color: '#fff',
                              borderColor: '#203097',
                            }
                          : {
                              bgcolor: 'background.paper',
                              color: 'text.primary',
                              borderColor: 'grey.300',
                            }),
                      }}
                    />
                  );
                })}
              </Stack>

              <TextField
                select
                variant="outlined"
                size="small"
                value={selectedLanguage ?? ''}
                disabled={languagesOptions.length < 2}
                onChange={e =>
                  handleEmbeddedLanguageChange(e.target.value as Locale)
                }
                sx={{
                  ...onboardingDeckCompactSelectTextFieldSx,
                  flex: '0 0 auto',
                  alignSelf: 'center',
                  ml: 'auto',
                  '&.MuiFormControl-root': {
                    width: 'auto',
                    flex: '0 0 auto',
                  },
                  '& .MuiInputBase-root': {
                    width: 'auto',
                  },
                  '& .MuiOutlinedInput-root': {
                    width: 'auto',
                    minWidth: 120,
                  },
                  '& .MuiSelect-select': {
                    py: '8px !important',
                    pl: '8px !important',
                    pr: '28px !important',
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 0.5 }}>
                        <OnboardingInputIcon icon={Globe} size={18} />
                      </InputAdornment>
                    ),
                  },
                  select: {
                    IconComponent: OnboardingSelectChevronIcon,
                  },
                }}
              >
                {languagesOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ position: 'relative' }}>
              <Typography component="label" sx={deckFieldLabelSx}>
                {t('Message')}
              </Typography>
              <TextField
                className="onboarding-review-message-field"
                value={message}
                onChange={handleMessageChange}
                multiline
                minRows={4}
                maxRows={12}
                fullWidth
                variant="outlined"
                placeholder={t('Enter your review invitation message')}
                sx={deckMessageFieldSx}
              />
              {reviewLink != null && (
                <Box
                  sx={{
                    mt: 0.75,
                    ml: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: 13,
                    lineHeight: 1.4,
                    color: 'text.secondary',
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box component="span" sx={{ flexShrink: 0 }}>
                    {t('Review link')}:
                  </Box>
                  <Box
                    component="a"
                    href={reviewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: 'primary.main',
                      textDecoration: 'underline',
                      '&:hover': { color: 'primary.dark' },
                    }}
                  >
                    {reviewLink}
                  </Box>
                  <Tooltip
                    title={linkCopied ? t('Link copied') : t('Copy link only')}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={handleCopyLink}
                      aria-label={t('Copy link only')}
                      sx={{
                        flexShrink: 0,
                        width: 24,
                        height: 24,
                        color: linkCopied
                          ? 'var(--rax-blue700)'
                          : 'text.secondary',
                        '&:hover': {
                          bgcolor:
                            'rgba(var(--mui-palette-primary-mainChannel) / 0.06)',
                          color: 'var(--rax-blue700)',
                        },
                      }}
                    >
                      {linkCopied ? (
                        <Check size={13} strokeWidth={2.5} />
                      ) : (
                        <Link2 size={13} strokeWidth={2} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {templateLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    top: 28,
                    bgcolor:
                      'rgba(var(--mui-palette-background-paperChannel) / 0.75)',
                    backdropFilter: 'blur(2px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px',
                  }}
                >
                  <CircularProgress size={24} thickness={5} />
                </Box>
              )}
            </Box>

            {hasUnsavedChanges && me != null && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveForFuture}
                    onChange={e => setSaveForFuture(e.target.checked)}
                    sx={{
                      color: 'grey.400',
                      '&.Mui-checked': { color: 'var(--rax-blue700)' },
                    }}
                  />
                }
                label={
                  <Stack
                    direction="row"
                    sx={{ gap: 0.5, alignItems: 'center' }}
                  >
                    <Box component="span">
                      {t('Save this message for future invites')}
                    </Box>
                    <Tooltip
                      title={t(
                        "Saved per review type and language. Switching either later will load that combo's saved version.",
                      )}
                      arrow
                      placement="top"
                      slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          color: 'text.secondary',
                          '&:hover': { color: 'text.primary' },
                        }}
                      >
                        <Info size={14} strokeWidth={2} />
                      </Box>
                    </Tooltip>
                  </Stack>
                }
                sx={{
                  m: 0,
                  alignItems: 'center',
                  color: 'text.secondary',
                  '& .MuiFormControlLabel-label': {
                    fontSize: 14,
                    lineHeight: 1.45,
                  },
                }}
              />
            )}

            {!userSlugLoading &&
              !isProfilePublished &&
              allowCopyBeforePublish && (
                <Stack
                  direction="row"
                  spacing={1.25}
                  sx={{
                    alignItems: 'flex-start',
                    p: 1.5,
                    borderRadius: '12px',
                    bgcolor: alpha('#56BEDB', 0.12),
                    border: '1px solid',
                    borderColor:
                      'rgba(var(--mui-palette-primary-mainChannel) / 0.14)',
                  }}
                >
                  <Box
                    sx={{
                      mt: 0.125,
                      color: 'var(--rax-blue700)',
                      flexShrink: 0,
                      display: 'flex',
                    }}
                  >
                    <Info size={18} strokeWidth={2} />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.45 }}
                  >
                    {t(
                      'Your profile goes live when you finish onboarding. You can still copy this link and send it to clients now.',
                    )}
                  </Typography>
                </Stack>
              )}

            <Button
              variant="contained"
              fullWidth
              disabled={!canCopy}
              onClick={handleCopyMessage}
              endIcon={
                copied ? (
                  <Check size={18} strokeWidth={2.5} />
                ) : (
                  <Copy size={18} strokeWidth={2} />
                )
              }
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 14,
                letterSpacing: 0.2,
                py: 1.25,
                borderRadius: '12px',
                boxShadow: canCopy
                  ? `0 4px 14px rgba(var(--mui-palette-primary-mainChannel) / 0.22)`
                  : 'none',
                background: canCopy
                  ? BRAND_GRADIENT
                  : (theme: Theme) => theme.palette.action.disabledBackground,
                color: canCopy ? '#fff' : 'action.disabled',
                '&:hover': {
                  background: canCopy
                    ? BRAND_GRADIENT_HOVER
                    : (theme: Theme) => theme.palette.action.disabledBackground,
                  boxShadow: canCopy
                    ? `0 6px 18px rgba(var(--mui-palette-primary-mainChannel) / 0.28)`
                    : 'none',
                },
                '&.Mui-disabled': {
                  background: (theme: Theme) =>
                    theme.palette.action.disabledBackground,
                  color: 'action.disabled',
                },
              }}
            >
              {copied ? t('Copied to clipboard') : t('Copy message')}
            </Button>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ width: '100%' }}
            >
              <Button
                variant="outlined"
                fullWidth
                disabled={!canCopy}
                onClick={handleShareWhatsapp}
                startIcon={<MessageCircle size={16} strokeWidth={2} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13.5,
                  py: 0.85,
                  borderRadius: '10px',
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'var(--rax-blue700)',
                    bgcolor:
                      'rgba(var(--mui-palette-primary-mainChannel) / 0.04)',
                  },
                }}
              >
                {t('Share via WhatsApp')}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                disabled={!canCopy}
                onClick={handleShareEmail}
                startIcon={<Mail size={16} strokeWidth={2} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13.5,
                  py: 0.85,
                  borderRadius: '10px',
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'var(--rax-blue700)',
                    bgcolor:
                      'rgba(var(--mui-palette-primary-mainChannel) / 0.04)',
                  },
                }}
              >
                {t('Share via email')}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {templateSavedToast && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              px: 2,
              py: 1,
              borderRadius: '8px',
              bgcolor: 'success.main',
              color: 'success.contrastText',
              typography: 'body2',
              fontWeight: 600,
              boxShadow: 2,
              maxWidth: 'calc(100% - 24px)',
              textAlign: 'center',
            }}
          >
            {t("Template saved — we'll preload this next time.")}
          </Box>
        )}

        <MutationErrorSnackbar
          error={saveError}
          onClose={() => setSaveError(null)}
        />
      </>
    );
  }

  return (
    <Stack sx={{ p: 2 }} spacing={2}>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('Select a language and customize your message')}
        </Typography>
        <Select
          value={selectedLanguage}
          variant="outlined"
          size="small"
          disabled={languagesOptions.length < 2}
          onChange={e => setSelectedLanguage(e.target.value as Locale)}
          sx={{
            background: (theme: Theme) => theme.palette.background.paper,
            minWidth: 140,
            '&.MuiInputBase-root': {
              paddingLeft: '6px',
            },
            '& .MuiSelect-select': {
              paddingLeft: 1,
            },
          }}
          startAdornment={
            <Globe size={20} color={grey[700]} style={{ marginLeft: 4 }} />
          }
        >
          {languagesOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Tabs
              value={selectedType}
              onChange={handleTypeChange}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                },
              }}
            >
              <Tab value="seller" label={t('Seller')} sx={{ flexGrow: 1 }} />
              <Tab value="buyer" label={t('Buyer')} sx={{ flexGrow: 1 }} />
            </Tabs>
          </Grid>

          <Grid size={12}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                value={message}
                onChange={handleMessageChange}
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                placeholder={t('Enter your review invitation message')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                  },
                }}
              />
              {templateLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor:
                      'rgba(var(--mui-palette-background-paperChannel) / 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={24} disableShrink />
                </Box>
              )}
            </Box>
            {hasUnsavedChanges && me != null && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveForFuture}
                    onChange={e => setSaveForFuture(e.target.checked)}
                  />
                }
                label={t('Save this message for future invites')}
                sx={{ mt: 1 }}
              />
            )}
          </Grid>

          {!userSlugLoading &&
            !isProfilePublished &&
            (allowCopyBeforePublish ? (
              <Grid size={12}>
                <Alert severity="info">
                  {t(
                    'Your profile goes live when you finish onboarding. You can still copy this link and send it to clients now.',
                  )}
                </Alert>
              </Grid>
            ) : (
              <Grid size={12}>
                <Alert severity="warning">
                  {t(
                    'You cannot get reviewed because your profile is not published.',
                  )}
                </Alert>
              </Grid>
            ))}

          <Grid size={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={!canCopy}
              endIcon={copied ? <Check size={18} /> : <Copy size={18} />}
              onClick={handleCopyMessage}
            >
              {userSlugLoading
                ? t('Loading...')
                : copied
                ? t('Copied to clipboard')
                : t('Copy message')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <MutationErrorSnackbar
        error={saveError}
        onClose={() => setSaveError(null)}
      />
    </Stack>
  );
};
